<template>
  <form
    class="newsletter-form"
    :class="[
      $blockClass(blockClass, 'newsletter'),
      {
        [classes.success]: state === 'SUCCESS',
        [classes.error]: state === 'ERROR',
        [classes.loading]: state === 'LOADING'
      }
    ]"
    :action="`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`"
    method="POST"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <input
      type="hidden"
      name="g"
      :value="list"
    >

    <div
      v-if="hasHeader"
      class="newsletter-form__header"
      :class="$blockClass(blockClass, 'newsletter-header')"
    >
      <!-- eslint-disable vue/no-v-text-v-html-on-component -->
      <component
        :is="titleElement"
        v-if="headings.title"
        :id="titleId"
        class="newsletter-form__title text-heading-s"
        :class="modifiers.title"
        v-text="headings.title"
      />

      <p
        v-if="headings.subtitle"
        class="newsletter-form__subtitle text-body-xs"
        :class="[
          $blockClass(blockClass, 'newsletter-subtitle'),
          modifiers.subtitle
        ]"
        v-text="headings.subtitle"
      />
    </div>

    <div
      class="newsletter-form__email"
      :class="$blockClass(blockClass, 'newsletter-email')"
    >
      <label
        class="visually-hidden"
        :for="getElementId('email')"
        v-text="$string('general.newsletter.email.label')"
      />

      <input
        :id="getElementId('email')"
        ref="email"
        :class="{
          'input-error': state === 'ERROR'
        }"
        type="email"
        name="email"
        :placeholder="$string('general.newsletter.email.placeholder')"
        required
        @keydown="handleKeydown"
      >

      <button
        v-show="state !== 'SUCCESS'"
        class="newsletter-form__submit"
        :disabled="state === 'LOADING'"
        v-text="$string('general.newsletter.submit')"
      />

      <icon-tick
        v-show="state === 'SUCCESS'"
        class="newsletter-form__tick"
        :class="$blockClass(blockClass, 'newsletter-tick')"
      />
    </div>

    <div
      v-if="hasFooter"
      class="newsletter-form__footer"
      :class="$blockClass(blockClass, 'newsletter-footer')"
    >
      <span
        v-if="state === 'ERROR'"
        class="newsletter-form__error text-body-3xs"
        v-text="$string('general.newsletter.error')"
      />

      <div
        v-if="disclaimer"
        class="newsletter-form__disclaimer text-body-3xs rte"
        :class="[
          $blockClass(blockClass, 'newsletter-disclaimer'),
          modifiers.disclaimer
        ]"
        v-html="disclaimer"
      />
    </div>
  </form>
</template>

<script>

/**
 * Vue: Newsletter form (newsletter-form)
 * -----------------------------------------------------------------------------
 * A form for subscribing to a Klaviyo newsletter list.
 *
 * @param {String} [title] - The form title.
 * @param {String} [subtitle] - The form subtitle.
 * @param {String} [list] - The Klaviyo list identifier.
 * @param {String} [disclaimer] - The disclaimer text displayed below.
 * @param {String} [blockClass] - Block level class to add to elements.
 * @param {Object} [modifiers] - Classes to add to selected elements.
 * @param {String} [modifiers.title] - Classes to add to the title.
 * @param {String} [modifiers.subtitle] - Classes to add to the subtitle.
 * @param {String} [modifiers.disclaimer] - Classes to add to the disclaimer.
 * @param {String} [titleElement] - The element used for the title.
 *
 */
import classes from '~/config/classes'

import IconTick from '~icons/misc/tick.svg'

export default {
  name: 'NewsletterForm',

  components: {
    IconTick,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    list: {
      type: String,
      default: '',
    },
    disclaimer: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    modifiers: {
      type: Object,
      default: () => ({
        title: '',
        subtitle: '',
        disclaimer: '',
      }),
    },
    titleId: {
      type: String,
      default: '',
    },
    titleElement: {
      type: String,
      default: 'p',
    },
  },

  emits: ['state'],

  data() {
    return {
      classes,
      state: 'IDLE',
      companyId: this.$variable('klaviyo.companyId'),
    }
  },

  computed: {

    /**
     * Returns if the component has the header element.
     * @returns {Boolean}
     */
    hasHeader() {
      return this.headings.title || this.headings.subtitle
    },

    /**
     * Returns if the component has the footer element.
     * @returns {Boolean}
     */
    hasFooter() {
      return this.disclaimer || this.state === 'ERROR'
    },

    /**
     * Returns the dynamic object of headings.
     * @returns {Object}
     */
    headings() {
      return {
        title: this.state === 'SUCCESS'
          ? this.$string('general.newsletter.success.title')
          : this.title,
        subtitle: this.state === 'SUCCESS'
          ? this.$string('general.newsletter.success.subtitle')
          : this.subtitle,
      }
    },
  },

  watch: {

    /**
     * Watches for changes to the state, and emits.
     */
    state() {
      this.$emit('state', this.state)
    },
  },

  methods: {

    /**
     * Returns a unique identifier for the given element, scoped to the list.
     * @param {String} element - The element name.
     * @returns {String}
     */
    getElementId(element) {
      return `${this.blockClass ?? 'newsletter-form'}-${this.list}-${element}`
    },

    /**
     * Handles the form submit event.
     * @param {Object} event - The event payload.
     */
    async handleSubmit(event) {
      if (this.state === 'LOADING') {
        return
      }

      if (!this.$refs.email.validity.valid) {
        this.state = 'ERROR'
        return
      }

      this.state = 'LOADING'

      try {
        const formData = new FormData(event.target)
        const formattedData = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'footer',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: formData.get('email'),
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: formData.get('g'),
                },
              },
            },
          },
        }

        const response = await fetch(event.target.action, {
          headers: { revision: '2024-02-15', 'content-type': 'application/json' },
          method: event.target.method,
          body: JSON.stringify(formattedData),
        })

        if (response.status === 200 || response.status === 202) {
          this.state = 'SUCCESS'
          return
        }

        this.state = 'ERROR'

      } catch {
        this.state = 'ERROR'
      }
    },

    /**
     * Handles the email input key down event.
     */
    handleKeydown() {
      if (this.state === 'IDLE') {
        return
      }

      this.resetState()
    },

    /**
     * Resets the component state.
     */
    resetState() {
      this.state = 'IDLE'
    },
  },
}
</script>

<style lang="scss">
@import './newsletter-form';
</style>
